import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Home } from "lucide-react";

import { Alert } from "@/components/Alert";
import { Field } from "@/components/Field";
import { PasswordInput } from "@/components/PasswordInput";
import { resolvePostLoginPath } from "@/auth/homePath";
import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function LoginPage() {
  const { isAuthenticated, isLoading, signIn, sessionExpired, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;

  const [form, setForm] = useState({ email: "", password: "" });
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  if (!isLoading && isAuthenticated) {
    return <Navigate to={resolvePostLoginPath(user, from)} replace />;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const signedInUser = await signIn(form);
      navigate(resolvePostLoginPath(signedInUser, from), { replace: true });
    } catch (apiError) {
      const fieldMessage = apiError.fieldErrors
        ? Object.values(apiError.fieldErrors).flat()[0]
        : null;
      setError(fieldMessage ?? apiError.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <aside className="relative hidden flex-col justify-between border-r border-border bg-muted/40 p-10 lg:flex">
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-lg bg-primary text-sm font-semibold text-primary-foreground">
            HC
          </div>
          <div>
            <p className="text-sm font-semibold tracking-tight">Homecare Platform</p>
            <p className="text-xs text-muted-foreground">Agency management</p>
          </div>
        </div>

        <div className="max-w-md space-y-4">
          <div className="inline-flex items-center gap-2 rounded-md border border-border bg-background/80 px-2.5 py-1 text-xs text-muted-foreground">
            <Home className="size-3.5" />
            Built for home-care operations
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-balance">
            Scheduling, care plans, and team coordination in one place
          </h1>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Sign in to manage visits, caregivers, clients, and compliance for your
            agency — or the platform console if you are a superuser.
          </p>
          <ul className="grid gap-2 text-sm text-muted-foreground">
            <li className="flex gap-2">
              <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-primary" />
              Multi-agency tenancy with role-based access
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-primary" />
              Weekly scheduling and visit documentation
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-primary" />
              Care plans and audit-ready activity history
            </li>
          </ul>
        </div>

        <p className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} Homecare Platform
        </p>
      </aside>

      <main className="flex items-center justify-center p-6 sm:p-10">
        <Card className="w-full max-w-md gap-4 py-6 shadow-sm">
          <CardHeader className="space-y-1 px-6">
            <div className="mb-2 flex items-center gap-2 lg:hidden">
              <div className="flex size-8 items-center justify-center rounded-md bg-primary text-xs font-semibold text-primary-foreground">
                HC
              </div>
              <span className="text-sm font-semibold">Homecare Platform</span>
            </div>
            <CardTitle className="text-xl">Welcome back</CardTitle>
            <CardDescription>
              Sign in to access your organization dashboard.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4 px-6">
            <Alert type="error">{error}</Alert>
            {sessionExpired && !error ? (
              <Alert type="info">
                Your session ended, so we signed you out. Sign in to pick up where
                you left off.
              </Alert>
            ) : null}

            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              <Field label="Email address" name="email">
                {(props) => (
                  <Input
                    {...props}
                    type="email"
                    autoComplete="username"
                    required
                    placeholder="name@agency.com"
                    value={form.email}
                    onChange={handleChange}
                  />
                )}
              </Field>

              <Field label="Password" name="password">
                {(props) => (
                  <PasswordInput
                    {...props}
                    autoComplete="current-password"
                    required
                    placeholder="••••••••"
                    value={form.password}
                    onChange={handleChange}
                  />
                )}
              </Field>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember-me"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked === true)}
                />
                <Label htmlFor="remember-me" className="font-normal">
                  Remember me
                </Label>
              </div>

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
